import { AfterViewInit, Component, OnInit, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from 'src/app/base/base-component';
import { Emprunt } from 'src/app/entites/emprunt';
import { EmpruntService } from 'src/app/services/emprunt.service';
import { ProfilService } from 'src/app/services/profil-service';
import { AppMainComponent } from 'src/app/app.main.component';
import { DonneesBaseService } from 'src/app/services/donnees-base-service';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Statut } from 'src/app/entites/statut';
import { AjoutEmpruntsComponent } from './ajout-emprunts.component';
import { DialogService } from 'primeng/dynamicdialog';
import { DefinitionColonne } from 'src/app/entites/definition-colonne';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { SortMeta, FilterService } from 'primeng/api';
import { EStatuts } from 'src/app/entites/enums/statuts';
import { Table } from 'primeng/table';
import { ViewChild } from '@angular/core';
import { EmployeEntite } from 'src/app/entites/employe';
import { Emplacement } from 'src/app/entites/emplacement';

@Component({
  selector: 'app-emprunts',
  templateUrl: './emprunts.component.html',
  styleUrl: './emprunts.component.scss',
  providers: [DialogService]
})
export class EmpruntsComponent extends BaseComponent implements OnInit, AfterViewInit {

  emprunts: Emprunt[] = [];
  statuts: Statut[] = [];
  
  tableParams = this.createTableFilter();

  loadingEvent: WritableSignal<HttpEvent<any>> = signal(null);
  loadingError: WritableSignal<string> = signal(null);  

  employes: EmployeEntite[] = [];
  emplacements: Emplacement[] = [];
  
  /** Combined filter options: employees and emplacements */
  employeEmplacementOptions: { label: string; value: string; type: 'employe' | 'emplacement' }[] = [];
  
  /** Selected assignations for the filter */
  selectionAssignations: string[] = [];
  
  sortMeta: SortMeta[] = [
    { field: 'isArchive', order: 1 },
    { field: 'nbJoursEnRetard', order: -1 },
    { field: 'dateAffectation', order: -1 },
    { field: 'empruntId', order: -1 }
  ];
  globalFilter: string = '';
  
  @ViewChild('dt') tableEmprunts: Table;

  constructor(private empruntService: EmpruntService, 
    sdb: DonneesBaseService,
    profilService: ProfilService, 
    router: Router, 
    route: ActivatedRoute,
    appMain: AppMainComponent,
    private dialogService: DialogService,
    private breadcrumbService: BreadcrumbService,
    private filterService: FilterService) {
    super(sdb, profilService, route, router, appMain);
    this.breadcrumbService.setItems([
      { label: 'Emprunts' }
    ]);
    
    // Register custom filter for employee/emplacement that checks employee ID or emplacement name
    this.filterService.register('employeEmplacementFilter', (value: string, filter: string[]): boolean => {
      if (!filter || filter.length === 0) return true;
      if (!value) return false;
      return filter.some(f => value.includes(f));
    });
  }

  ngOnInit(): void {
    this.verifierAccesPage('emprunt');
    this.chargerEmprunts();
    this.statuts = this.sdb.Statuts().filter(x => x.statutId == EStatuts.EnRetard || x.statutId == EStatuts.Actif || x.statutId == EStatuts.EnAttente || x.statutId == EStatuts.Archive);
    this.employes = this.sdb.Employes();
    this.emplacements = this.sdb.Emplacements();
    
    // Build combined filter options: employees and emplacements
    this.employeEmplacementOptions = [
      ...this.emplacements.map(e => ({ label: e.nom, value: e.nom, type: 'emplacement' as const })),
      ...this.employes.map(e => ({ label: e.prenom + ' ' + e.nom, value: e.prenom + ' ' + e.nom, type: 'employe' as const }))
    ];
    // Initialize column definitions
    this.colonnesDisponibles = [
      { champ: 'item', entete: 'Item', afficheParDefaut: true },
      { champ: 'emplacementDepart', entete: 'Départ', afficheParDefaut: true },
      { champ: 'employe', entete: 'Employé', afficheParDefaut: true },
      { champ: 'statut', entete: 'Statut', afficheParDefaut: true },
      { champ: 'dateAffectation', entete: 'Date de début', afficheParDefaut: true },
      { champ: 'dateRetour', entete: 'Date de retour', afficheParDefaut: true },
      { champ: 'emplacementRetour', entete: 'Emplacement retour', afficheParDefaut: false },
      { champ: 'employeRetour', entete: 'Employé retour', afficheParDefaut: false },
      { champ: 'nbEmpruntsEnfants', entete: 'Nb. d\'enfants', afficheParDefaut: false }
    ];
    
    this.colonnesSelectionnees = this.colonnesDisponibles.filter(x => x.afficheParDefaut);
  }

  private chargerEmprunts() {
    this.DemarrerChargement();
    this.empruntService.getEmprunts().subscribe({
      next: (event) => {
        if (event.type === HttpEventType.Response) {
          this.CompleterChargement(); 
          this.emprunts = event.body;
          this.emprunts.forEach(emprunt => {
            emprunt.item.numeroDescription = emprunt.item.numero + ' - ' + emprunt.item.description;
            // Add computed field for departure that includes both location and employee possibilities
            emprunt.departDescription = emprunt?.emplacementDepart?.nom || 
              (emprunt?.employeDepart ? (emprunt.employeDepart.prenom + ' ' + emprunt.employeDepart.nom) : '');
            emprunt.nbJoursEnRetard = emprunt.statut.statutId == EStatuts.EnRetard ? this.getNombreJoursEnRetard(emprunt) : 0;
            emprunt.isArchive = emprunt.statut.statutId == EStatuts.Archive;
            
            // Build computed field for employee filtering - includes employee name AND their current emplacement
            let employeFilter = emprunt.employe 
              ? emprunt.employe.prenom + ' ' + emprunt.employe.nom 
              : '';
            if (emprunt.employe && emprunt.emplacementCourantEmploye?.nom) {
              employeFilter += '|' + emprunt.emplacementCourantEmploye.nom;
            }
            emprunt['employeFilter'] = employeFilter;
          });
        }
        this.loadingEvent.set(event);
      },
      error: (error) => {
        this.loadingError.set(error.error);
        this.CompleterChargement();
      }
    });
  }

  ngAfterViewInit(): void {
    this.globalFilter = this.tableParams.getValue().globalFilter;
    if (this.tableParams.getValue().multiSortMeta) {
        this.sortMeta = this.tableParams.getValue().multiSortMeta;
    } else {
        this.sortMeta = [
            { field: 'isArchive', order: 1 },
            { field: 'nbJoursEnRetard', order: -1 },
            { field: 'dateAffectation', order: -1 },
            { field: 'empruntId', order: -1 }
        ];
    }
    this.chargerColonnesSelectionnees();
}

  nouveauEmprunt(): void {
    this.dialogService.open(AjoutEmpruntsComponent, {
      header: 'Nouvel emprunt',
      width: this.LargeurDialogue,
      style: {
        'overflow-y': 'visible'
      }
    });
  }

  public getNombreJoursEnRetard(emprunt: Emprunt): number {
    if (!emprunt.dateRetour) {
      return 0;
    }
    const dateRetour = new Date(emprunt.dateRetour);
    const TODAY = new Date();
    
    dateRetour.setHours(0, 0, 0, 0);
    TODAY.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(dateRetour.getTime() - TODAY.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  surChangementColonnes(event: any): void {
    this.colonnesSelectionnees = event.value;
  }
}
